"use client";

import { LuTicket, LuGlobe, LuCopy } from "react-icons/lu";
import { isSameDay } from "date-fns";
import { MapLocation } from "./MapLocation";
import { EventLocation } from "./EventLocation";
import { GetEventFromSlug } from "../actions";
import { TimeDisplay } from "./EventTimeComp";
import { cn } from "~/utils/fontsConfig";
import { getEventStatusColor } from "../utils/colors";
import { useEventRSVPWrapper } from "~/app/providers";
import { FaUndoAlt } from "react-icons/fa";
import React, { useEffect, useRef, useState } from "react";
import { EventJourneyStatus, Prisma } from "@prisma/client";
import Modal from "~/components/v3/themed/Modal";
import { APP_URL } from "~/utils/constants";
import { toast } from "react-toastify";
import { Button } from "~/components/v4/themed/Button";
import { DateTime } from "luxon";
import { RecurrencePreview } from "./EventRecurrenceSheet";
import { RRule } from "rrule";
import VirtualEventLink from "./VirtualEventLink";
import EventStatusWarnings from "./EventStatusWarnings";
import { useSession } from "next-auth/react";
import { If } from "~/components/v3/Conditional";
import { InvalidVenueNotice } from "./InvalidVenueNotice";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { EventHiddenDateTime } from "./EventHiddenDateTime";

const getStatusText = (status: EventJourneyStatus) => {
    if (!status) return "";

    if (status === EventJourneyStatus.DRAFT_PROPOSED) {
        return "Draft";
    }

    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const EventDetails = ({
    event,
    isManagePage = false,
    customization,
}: {
    event: GetEventFromSlug;
    isManagePage?: boolean;
    customization?: CustomizableLayout & {
        infobox: {
            bg?: string;
            content?: string;
            label?: string;
        };
    };
}) => {
    const session = useSession();
    const [isRecurrenceDetailsOpen, setIsRecurrenceDetailsOpen] =
        useState(false);
    const rsvpWrapper = useEventRSVPWrapper();
    const isSameDayEvent = event.endTime
        ? isSameDay(event.startTime, event.endTime)
        : false;
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkVisibility = () => {
            if (!ref.current || !ref.current.parentElement) return;
            if (typeof window === "undefined") return;
            setIsVisible(
                window.getComputedStyle(ref.current.parentElement).display !==
                    "none",
            );
        };

        checkVisibility();

        // Optional: Re-check visibility on window resize or other events
        window.addEventListener("resize", checkVisibility);
        return () => window.removeEventListener("resize", checkVisibility);
    }, []);

    const isHost = event.hosts.some(
        (host) => host.user.id === session.data?.user.id,
    );

    const paidTickets = event.eventTickets.filter(
        (ticket) => ticket.price && ticket.price > 0,
    );

    const ticketName =
        paidTickets.length > 0
            ? `${paidTickets[0]?.price}${paidTickets.length > 1 ? "..." : ""}`
            : "Free";

    return (
        <div ref={ref} className="flex flex-col gap-4">
            <EventStatusWarnings event={event} />

            <h2
                className={cn(
                    "flex items-end gap-4 text-[20px] text-v3-N-300",
                    customization?.labelClassName,
                )}
            >
                Event details
                <If condition={isHost}>
                    <span
                        className={cn(
                            "hidden rounded-lg p-1 px-2 text-sm text-white",
                            getEventStatusColor(event.eventJourneyStatus),
                            !isManagePage && "md:block",
                        )}
                    >
                        {getStatusText(event.eventJourneyStatus)}
                    </span>
                </If>
            </h2>
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-[48px_1fr_48px] items-start gap-4">
                    {event.isTimeSet ? (
                        <React.Fragment>
                            <div
                                className={cn(
                                    "flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-v3-N-500",
                                    customization?.infobox?.bg,
                                )}
                            >
                                <span
                                    className={cn(
                                        "text-sm text-v3-N-300",
                                        customization?.inputLabelClassName,
                                    )}
                                >
                                    {event.startTime.toLocaleString("en-US", {
                                        month: "short",
                                        timeZone: "UTC",
                                    })}
                                </span>
                                <span
                                    className={cn(
                                        "text-base font-bold text-white",
                                        customization?.inputLabelClassName,
                                    )}
                                >
                                    {event.startTime.toLocaleString("en-US", {
                                        day: "numeric",
                                        timeZone: "UTC",
                                    })}
                                </span>
                            </div>

                            <div className="flex flex-col">
                                <div className="flex gap-1">
                                    <span
                                        className={cn(
                                            "text-white",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        {event.startTime.toLocaleString(
                                            "en-US",
                                            {
                                                weekday: isSameDayEvent
                                                    ? "long"
                                                    : "short",
                                                month: isSameDayEvent
                                                    ? "long"
                                                    : "short",
                                                day: "numeric",
                                                timeZone: "UTC",
                                            },
                                        )}
                                    </span>

                                    {event.endTime && !isSameDayEvent && (
                                        <span
                                            className={cn(
                                                "flex gap-1",
                                                customization?.descriptionClassName,
                                            )}
                                        >
                                            <span>-</span>

                                            <span>
                                                {event.endTime.toLocaleString(
                                                    "en-US",
                                                    {
                                                        weekday: "short",
                                                        month: "short",
                                                        day: "numeric",
                                                        timeZone: "UTC",
                                                    },
                                                )}
                                            </span>
                                        </span>
                                    )}
                                </div>

                                <TimeDisplay
                                    startTime={event.startTime}
                                    endTime={event.endTime}
                                    eventTimezone={event.timezone}
                                    className={
                                        customization?.descriptionClassName
                                    }
                                />
                            </div>
                        </React.Fragment>
                    ) : (
                        <EventHiddenDateTime />
                    )}

                    {event.recurrenceRule && (
                        <div
                            onClick={() => setIsRecurrenceDetailsOpen(true)}
                            className={cn(
                                "flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg bg-v3-N-500",
                                customization?.infobox?.bg,
                            )}
                        >
                            <FaUndoAlt
                                className={cn(
                                    "h-5 w-5 text-white",
                                    customization?.infobox?.content,
                                )}
                            />
                        </div>
                    )}
                </div>

                <span className="flex flex-col gap-2 md:hidden">
                    <EventLocation
                        event={event}
                        customization={customization}
                    />
                    {isManagePage && <InvalidVenueNotice event={event} />}
                </span>

                {/* Tickets Section */}
                <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                    <div
                        className={cn(
                            "flex h-12 w-12 items-center justify-center rounded-lg bg-v3-N-500",
                            customization?.infobox?.bg,
                        )}
                    >
                        <LuTicket
                            className={cn(
                                "h-5 w-5 text-white",
                                customization?.infobox?.content,
                            )}
                        />
                    </div>
                    <div className="flex flex-col">
                        <span
                            className={cn(
                                "text-white",
                                customization?.inputLabelClassName,
                            )}
                        >
                            Tickets
                        </span>
                        <span className=" flex items-center gap-1">
                            {ticketName !== "Free" && (
                                <span
                                    className={cn(
                                        "text-v3-N-300",
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    $
                                </span>
                            )}
                            <span
                                className={cn(
                                    "text-v3-N-300",
                                    customization?.descriptionClassName,
                                )}
                            >
                                {ticketName}
                            </span>
                        </span>
                    </div>
                </div>

                {/* Visibility Section */}
                <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                    <div
                        className={cn(
                            "flex h-12 w-12 items-center justify-center rounded-lg bg-v3-N-500",
                            customization?.infobox?.bg,
                        )}
                    >
                        <LuGlobe
                            className={cn(
                                "h-5 w-5 text-white",
                                customization?.infobox?.content,
                            )}
                        />
                    </div>
                    <div className="flex flex-col">
                        <span
                            className={cn(
                                "text-white",
                                customization?.inputLabelClassName,
                            )}
                        >
                            Visibility
                        </span>
                        <span
                            className={cn(
                                "text-v3-N-300",
                                customization?.descriptionClassName,
                            )}
                        >
                            {event.private ? "Private" : "Public"}
                        </span>
                    </div>
                </div>

                {/* Location Section */}
                <span
                    className={cn(
                        "hidden flex-col gap-2 md:flex",
                        !event.location && "mt-[-16px]", // - offset for gap-4
                    )}
                >
                    {event.location && (
                        <EventLocation
                            event={event}
                            customization={customization}
                        />
                    )}
                    {isManagePage && <InvalidVenueNotice event={event} />}
                </span>
                <VirtualEventLink event={event} customization={customization} />
                <MapLocation event={event} customization={customization} />
                {isVisible && rsvpWrapper}
            </div>
            <RecurrenceDetailsnModal
                open={isRecurrenceDetailsOpen}
                onClose={() => setIsRecurrenceDetailsOpen(false)}
                event={event}
            />
        </div>
    );
};

const RecurrenceDetailsnModal = ({
    open,
    onClose,
    event,
}: {
    open: boolean;
    onClose: () => void;
    event: Prisma.EventGetPayload<{
        select: {
            recurrenceRule: true;
            seriesSlug: true;
            startTime: true;
            name: true;
        };
    }>;
}) => {
    if (!event.recurrenceRule) return null;
    const rrule = RRule.fromString(event.recurrenceRule);
    const dates = rrule
        .all(function (date, i) {
            return i < 4;
        })
        .filter(
            (date) =>
                DateTime.fromJSDate(date).startOf("day") >
                DateTime.now().startOf("day"),
        )
        .map((date) => DateTime.fromJSDate(date));
    return (
        <Modal
            title="Recurring event"
            open={open}
            onOpenChange={onClose}
            icon={<FaUndoAlt className="h-5 w-5" />}
        >
            <div className="flex flex-col gap-4">
                <p>
                    This is a recurring event. You can find dates of few
                    upcoming occurrences below.
                </p>
                <RecurrencePreview
                    dates={dates}
                    eventName={event.name}
                    eventStartTime={event.startTime}
                />
                {event.seriesSlug && (
                    <div className="mt-4 flex flex-col gap-1 text-sm">
                        <span>Series URL:</span>
                        <code className="flex items-center gap-2 rounded bg-muted px-2 py-1">
                            <a
                                target="_blank"
                                className="truncate hover:underline"
                                href={`${APP_URL}/series/${event.seriesSlug}`}
                            >
                                {`${APP_URL}/series/${event.seriesSlug}`}
                            </a>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="ml-2"
                                onClick={() => {
                                    navigator.clipboard.writeText(
                                        `${APP_URL}/series/${event.seriesSlug}`,
                                    );
                                    toast.success(
                                        "Series URL copied to clipboard",
                                    );
                                }}
                            >
                                <LuCopy className="h-4 w-4" />
                            </Button>
                        </code>
                    </div>
                )}
            </div>
        </Modal>
    );
};
